import { Injectable } from '@angular/core';
import { AppConfig } from '../../types/config.types';
import * as configData from '../../config/config.json';

@Injectable({
  providedIn: 'root'
})
export class GlobalConfigService {
  private readonly config: AppConfig = configData as AppConfig;

  constructor() {}

  /**
   * Gets the logging base URL by removing '/chatbot-common' from the API base URL
   * @returns The logging base URL
   */
  get loggingBaseUrl(): string {
    try{
      const apiBaseUrl = this.config?.apiBaseUrl;
      return apiBaseUrl || '';
    }catch(error){
      return '';
    }
  }

  get debugLoggingMode(): boolean {
    try {
      return this.config?.DEBUG_LOGGING_MODE || false;
    } catch(error) {
      return false;
    }
  }

  /**
   * Generates a UUID v4 string for request identification.
   * Used for API tracing via 'API-Request-ID' header and WebSocket 'request_id' parameter.
   * Enables end-to-end traceability across frontend and backend systems.
   * 
   * @returns A UUID v4 string (e.g., "550e8400-e29b-41d4-a716-446655440000")
   */
  generateRequestId(): string {
    // Use native crypto.randomUUID() if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // More secure fallback using crypto.getRandomValues()
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      // Convert to UUID format
      const hex = Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
      return [
        hex.slice(0, 8),
        hex.slice(8, 12),
        '4' + hex.slice(13, 16), // Version 4 UUID
        ((parseInt(hex.slice(16, 17), 16) & 0x3) | 0x8).toString(16) + hex.slice(17, 20), // Variant bits
        hex.slice(20, 32)
      ].join('-');
    }
    // Last resort fallback for environments without crypto
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0; // NOSONAR: Safe - fallback only when crypto API unavailable
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  /**
   * Formats a log message with request ID prefix for traceability.
   * 
   * @param message - The log message
   * @param requestId - The request ID to include
   * @returns Formatted message with request ID prefix
   */
  formatLogMessageWithRequestId(message: string, requestId: string): string {
    return `[RequestID: ${requestId}] ${message}`;
  }
}